import React, { useContext, useEffect, useState } from "react";
import { DatePicker, Form, Input, Card, Table, Button, Select } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { HomeOutlined, RollbackOutlined } from "@ant-design/icons";
import { getBranches, getRefundsByItems } from "../../../network/network";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { CSVLink } from "react-csv";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import { UserType } from "../../../configs/constants";
import { AppContext } from "../../../context/AppContextProvider";
import { handleApiError } from "../../../utils/errorHandler";

const RefundsByItems = () => {
  const { authUser } = useContext(AppContext);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [reportTableData, setReportTableData] = useState([]);
  const [searchParams, setSearchParams] = useState([]);
  const [totalAmont, setTotalAmont] = useState([]);
  const [branchesData, setBranchesData] = useState([]);
  const { t } = useTranslation();
  const [searchFormRef] = Form.useForm();
  const { RangePicker } = DatePicker;

  useEffect(() => {
    setIsDataLoading(true);
    getRefundsByItems(searchParams)
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setReportTableData(response.data.data.refunds_by_items);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [searchParams, t]);

  useEffect(() => {
    setIsDataLoading(true);
    getBranches({ per_page: -1 })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setBranchesData(response.data.data.branches);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [t]);

  useEffect(() => {
    let total = 0;
    reportTableData.forEach(({ net_amount }) => {
      total = total + net_amount;
    });
    setTotalAmont(total);
  }, [reportTableData]);

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: null,
      breadcrumbName: t("reports") + " / " + t("refunds_by_items"),
      icon: <RollbackOutlined />,
    },
  ];

  const reportTableColumns = [
    {
      title: t("product"),
      dataIndex: "name",
    },
    {
      title: t("category"),
      dataIndex: "category",
    },
    {
      title: t("unit"),
      dataIndex: "unit",
    },
    {
      title: t("refund_quantities"),
      dataIndex: "quantity_sold",
    },
    {
      title: t("gross_refund"),
      render: (record) => {
        return <CurrencyFormatter value={record.net_amount} />;
      },
    },
  ];

  const handleSearch = (values) => {
    let search_params = false;
    if (values.dates) {
      let startDateUtc = moment.utc(values.dates[0]).set({
        date: values.dates[0].date() - 1,
        month: values.dates[0].month(),
        year: values.dates[0].year(),
        hour: 19,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      let endDateUtc = moment.utc(values.dates[1]).set({
        date: values.dates[1].date(),
        month: values.dates[1].month(),
        year: values.dates[1].year(),
        hour: 18,
        minute: 59,
        second: 59,
        millisecond: 0,
      });

      search_params = {
        search_start_date: startDateUtc.format(),
        search_end_date: endDateUtc.format(),
      };
    }
    if (values.filter_by_branch) {
      search_params = {
        filter_by_branch: values.filter_by_branch,
        ...search_params,
      };
    }

    if (search_params) {
      setSearchParams(search_params);
    } else {
      setSearchParams([]);
    }
  };
  const handleReset = () => {
    // Reset Form Fields
    searchFormRef.resetFields();
    // Reset Transactions List
    setSearchParams([]);
  };

  const searchForm = (
    <>
      <Form
        name="search-report-form"
        layout="inline"
        onFinish={handleSearch}
        form={searchFormRef}
      >
        <Form.Item name="dates" label={t("date_range")}>
          <RangePicker allowClear format="DD/MM/YYYY" />
        </Form.Item>
        {authUser.type !== UserType.Employee && (
          <Form.Item name="filter_by_branch" label={t("branch")}>
            <Select
              allowClear
              className="app-w-100px"
              options={branchesData.map((branch) => {
                return {
                  label: branch.name,
                  value: branch.name,
                };
              })}
            />
          </Form.Item>
        )}
        <Form.Item>
          <Input
            type="submit"
            value={t("search")}
            className="app-search-submit"
          />
        </Form.Item>
        <Form.Item>
          <Input type="button" value={t("reset")} onClick={handleReset} />
        </Form.Item>
      </Form>
    </>
  );

  const csvData = [
    [
      t("product"),
      t("category"),
      t("unit"),
      t("refund_quantities"),
      t("gross_refund"),
    ],
    ...reportTableData.map(
      ({ name, category, unit, quantity_sold, net_amount }) => [
        name,
        category,
        unit,
        quantity_sold,
        Number(net_amount).toFixed(2),
      ],
    ),
    [],
    ["", "", "", "", t("total_refunds"), Number(totalAmont).toFixed(2)],
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("refunds_by_items")}
          breadcrumb={CustomBreadcrumb}
          ghost={false}
          footer={searchForm}
          extra={[
            <Button type="primary" key="download-report">
              <CSVLink
                data={csvData}
                filename={
                  t("refunds_by_items") +
                  " " +
                  moment().format("MM-DD-YYYY HH-mm") +
                  ".csv"
                }
                target="_blank"
              >
                {t("download_report")}
              </CSVLink>
            </Button>,
          ]}
        />
      </Card>
      <Card>
        <Table
          bordered={true}
          columns={reportTableColumns}
          dataSource={reportTableData}
          loading={isDataLoading}
          rowKey="name"
          pagination={false}
          summary={(pageData) => {
            let totalRefunds = 0;

            pageData.forEach(({ net_amount }) => {
              totalRefunds = totalRefunds + net_amount;
            });

            return (
              <>
                <Table.Summary.Row
                  className="app-summary-cell"
                >
                  <Table.Summary.Cell colSpan={4}>
                    {t("total_refunds")}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell>
                    {<CurrencyFormatter value={totalRefunds} />}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </>
            );
          }}
        />
      </Card>
    </>
  );
};

export default RefundsByItems;
